import { NeoInputMode } from "./enums";
import type { AccountStatus, AccountUsageIntent, Domain, OwnerScope } from "@prisma/client";
import z from "zod";

/**
 * Account data for serialization
 */
export interface Account {
  id: number;
  accountName: string;
  email: string;
  firstName: string;
  surname: string;
  accountStatus: AccountStatus;
  isMfaEnabled: boolean;
  isAccessible: boolean;
  neoIntroSeenAt: string | null;
  usageIntent: AccountUsageIntent;
  hidePersonalMembers: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Input for creating a new account
 */
export interface AccountCreateInput {
  accountName: string;
  email: string;
  password: string | { passwordHash: string };
  firstName: string;
  surname: string;
  domain?: Domain;
  accountStatus?: AccountStatus;
  isAccessible?: boolean;
  isMfaEnabled?: boolean;
  usageIntent?: AccountUsageIntent;
  hidePersonalMembers?: boolean;
  stripeCustomerId?: string;
}

export const accountPreferencesUpdateSchema = z.object({
  hidePersonalMembers: z.boolean(),
});
export type AccountPreferencesUpdateInput = z.infer<typeof accountPreferencesUpdateSchema>;

export interface LoginLandingMemberView {
  memberId: number;
  email: string;
  hubName: string;
  landingFilterId: number | null;
  landingFilterName: string | null;
  landingFilterIsTab: boolean | null;
  autoAnchorUploads: boolean;
  neoDefaultInputMode: NeoInputMode;
  observationNewAfterRecordings: number | null;
  observationNewAfterPhotos: number | null;
  observationNewAfterVideos: number | null;
  isCurrentMember: boolean;
}

export interface AccountLoginLanding {
  landingMemberId: number | null;
  members: LoginLandingMemberView[];
}

export interface LoginLandingViewOption {
  id: number;
  name: string;
  shortName: string | null;
  isTab: boolean;
  ownerScope: OwnerScope;
}

export const accountLoginLandingUpdateSchema = z
  .object({
    landingMemberId: z.number().int().positive().nullable(),
  })
  .strict();
export type AccountLoginLandingUpdateInput = z.infer<typeof accountLoginLandingUpdateSchema>;

export const memberLandingViewUpdateSchema = z
  .object({
    filterId: z.number().int().positive().nullable(),
  })
  .strict();
export type MemberLandingViewUpdateInput = z.infer<typeof memberLandingViewUpdateSchema>;

export const memberAutoAnchorUploadsUpdateSchema = z
  .object({
    autoAnchorUploads: z.boolean(),
  })
  .strict();
export type MemberAutoAnchorUploadsUpdateInput = z.infer<typeof memberAutoAnchorUploadsUpdateSchema>;

export const memberNeoDefaultInputModeUpdateSchema = z
  .object({
    neoDefaultInputMode: z.nativeEnum(NeoInputMode),
  })
  .strict();
export type MemberNeoDefaultInputModeUpdateInput = z.infer<typeof memberNeoDefaultInputModeUpdateSchema>;

const observationThresholdSchema = z.number().int().min(1).max(10).nullable();

export const memberObservationNewThresholdsUpdateSchema = z
  .object({
    observationNewAfterRecordings: observationThresholdSchema,
    observationNewAfterPhotos: observationThresholdSchema,
    observationNewAfterVideos: observationThresholdSchema,
  })
  .strict();
export type MemberObservationNewThresholdsUpdateInput = z.infer<typeof memberObservationNewThresholdsUpdateSchema>;
